import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as Icon from '../../components/icons';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDataInfo?: boolean;
  dataSize?: number;
  maxDataSize?: number;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

const ChartErrorFallback: React.FC<{
  error?: Error;
  message?: string;
  dataSize?: number;
  maxDataSize?: number;
  onRetry?: () => void;
}> = ({ error, message, dataSize, maxDataSize, onRetry }) => {
  const { t } = useTranslation('feed');

  const isDataSizeIssue = dataSize && maxDataSize && dataSize > maxDataSize;
  const isStackOverflow = error?.message?.includes(
    'Maximum call stack size exceeded',
  );

  let errorMessage = message || t`Chart failed to render`;
  let suggestion = t`Try refreshing the page or reducing the data range`;

  if (isDataSizeIssue || isStackOverflow) {
    errorMessage = t`Dataset too large to display`;
    suggestion = t`Try filtering the data or selecting a smaller time range. When "ALL" is selected, the dataset becomes very large.`;
    
    // Add specific guidance for stack overflow
    if (isStackOverflow) {
      errorMessage = t`Chart crashed due to dataset size`;
      suggestion = t`The dataset is too large for the chart to render. Try selecting specific groups instead of "ALL" or reduce the time range.`;
    }
  }

  return (
    <div
      data-component="chart-error-boundary"
      className="bg-menu/50 flex h-full min-h-[200px] w-full flex-col items-center justify-center p-8 text-center border-2 border-red-500 rounded-lg"
    >
      <Icon.CircleInfo className="mb-4 h-12 w-12 fill-current" />
      <h3 className="font-semibol mb-2 text-lg">{errorMessage}</h3>
      <p className="mb-4 text-sm">{suggestion}</p>

      {dataSize && (
        <div className="mb-4 text-xs">
          {`${t('Data points')}: ${dataSize.toLocaleString()}`}
          {maxDataSize && ` / ${maxDataSize.toLocaleString()} max`}
        </div>
      )}

      <div className="flex gap-2">
        {onRetry && (
          <button
            onClick={onRetry}
            className="bg-menu-active text-menu-active-text rounded px-4 py-2 text-sm"
          >
            {t`Retry`}
          </button>
        )}
        <button
          onClick={() => window.location.reload()}
          className="bg-menu-secondary text-menu-text rounded px-4 py-2 text-sm"
        >
          {t`Refresh Page`}
        </button>
      </div>

      {error && process.env.NODE_ENV === 'development' && (
        <details className="bg-app-bg mt-4 w-full max-w-full">
          <summary className="cursor-pointer text-xs">{t`Debug Info`}</summary>
          <pre className="bg-app-panel-dark mt-2 h-full overflow-auto rounded p-2 text-left text-xs">
            {error.stack}
          </pre>
        </details>
      )}
    </div>
  );
};

class ChartErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Chart Error:', error);
    console.error('Chart Error Info:', errorInfo);

    // Log performance info
    if (this.props.dataSize) {
      console.warn(`Chart failed with ${this.props.dataSize} data points`);
    }

    this.setState({ error, errorInfo });
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ChartErrorFallback
          error={this.state.error}
          message={this.props.fallbackMessage}
          dataSize={this.props.dataSize}
          maxDataSize={this.props.maxDataSize}
          onRetry={() => this.setState({ hasError: false, error: undefined })}
        />
      );
    }

    return this.props.children;
  }
}

export default ChartErrorBoundary;
